import { useEffect, useCallback, useState } from 'react'
import { useTheme, useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import Logo from 'ui/src/assets/images/Logo.svg?react'
import CompleteLogo from 'ui/src/assets/images/ProtexxaLogo.svg?react'
import Box from 'ui/src/components/Box'
import Button from 'ui/src/components/Button'
import LanguageSelector from 'ui/src/components/LanguageSelector'
import Services from 'services'
import globalStore from 'store'
import { toastStyle } from 'util/toast'
import { StyledBox, ButtonsContainer, StyledContainer } from './styles'
import { useFlags } from 'launchdarkly-react-client-sdk'
import generateErrorMessage from 'util/errorMessage'
import Typography from '@mui/material/Typography'
import ConciergeIcon from 'ui/src/assets/images/SignupFlow/concierge.svg?react'

export default function Header() {
  const flags = useFlags()
  const navigate = useNavigate()
  const user = globalStore(state => state.user)
  const register = globalStore(state => state.register)
  const logout = globalStore(state => state.logout)
  const [haveCompletedRegister, setHaveCompletedRegister] = useState<boolean>(false)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const { t } = useTranslation()

  const landingPage = process.env.REACT_APP_LANDING_PAGE || ''

  const handleLanguageChange = useCallback(
    async (language: string) => {
      const langCode = language.split('-')[0]
      if (user) await Services.User.updateLanguage(langCode)
    },
    [user],
  )

  useEffect(() => {
    if (!user || !register) {
      setHaveCompletedRegister(false)
    } else {
      setHaveCompletedRegister(register.status === 'completed')
    }
  }, [user, register, haveCompletedRegister])

  const handleSignOut = useCallback(async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      if (error instanceof Error) toast.error(generateErrorMessage(error.message), toastStyle)
    }
  }, [logout, navigate])

  const ButtonEnter = () => {
    return (
      <ButtonsContainer>
        <Button
          sx={{ textTransform: 'none', marginRight: 3, height: 40 }}
          type="button"
          variant="text"
          onClick={handleSignOut}
        >
          {t('default.logout')}
        </Button>
        <Button
          sx={{ textTransform: 'none', height: 40 }}
          type="button"
          variant="contained"
          onClick={() => {
            navigate('/redirect')
          }}
        >
          {haveCompletedRegister ? t('default.GoDashboard') : t('default.completeProfile')}
        </Button>
      </ButtonsContainer>
    )
  }

  const HeaderButtons = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, md: 2 },
        }}
      >
        {flags.featureB2CSignup && (
          <Button
            sx={{ height: 40 }}
            type="button"
            variant="outlined"
            onClick={() => {
              navigate('/signup')
            }}
          >
            {t('auth.createAccount')}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <StyledBox>
      <StyledContainer>
        <Box
          sx={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        >
          {isSmallScreen ? (
            <Logo onClick={() => (window.location.href = landingPage)} />
          ) : (
            <CompleteLogo onClick={() => (window.location.href = landingPage)} />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isSmallScreen ? 0.5 : 2,
          }}
        >
          <Box
            onClick={() => {
              navigate('/concierge')
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
            }}
          >
            <ConciergeIcon
              style={{
                cursor: 'pointer',
                marginLeft: isSmallScreen ? '0.3125rem' : 0, //5px
                height: '1.09375rem', //17.5px
                width: 'auto',
              }}
            />
            <Typography
              variant="body3"
              sx={{
                color: 'primary.600',
                display: { xs: 'none', sm: 'none', md: 'block' },
              }}
            >
              Concierge
            </Typography>
          </Box>
          <LanguageSelector onChange={handleLanguageChange} />
          {user ? <ButtonEnter /> : <HeaderButtons />}
        </Box>
      </StyledContainer>
    </StyledBox>
  )
}
